import { state, api, $, $$, toast, escHtml } from './core';
import { askConfirm } from './dialogs';
import type { EquipmentItem, QualityCheck, QualityDoc, QualityField, QualityTemplate } from './types';

/* ========== PRODUCT QUALITY MODULE ========== */
export let _pqLineId: string | null = '', _pqMoldId = '', _pqProduct: string | null = '', _pqTab: string | undefined = 'spec',
    _pqMolds: EquipmentItem[] = [];

export function openProductQuality(lineId: string|null, product: string) {
    _pqLineId = lineId;
    _pqProduct = product || lineId;
    _pqMoldId = '';
    _pqTab = 'spec';
    const ov = document.getElementById('pq-overlay');
    ov!.classList.remove('hidden');
    document.getElementById('pq-line-id')!.textContent = lineId;
    document.getElementById('pq-product-name')!.textContent = product || '';
    document.querySelectorAll('#pq-tabs .detail-tab').forEach(t => {
        t.classList.toggle('active', (t as HTMLElement).dataset.pqtab === 'spec');
        (t as HTMLElement).onclick = () => {
            _pqTab = (t as HTMLElement).dataset.pqtab;
            document.querySelectorAll('#pq-tabs .detail-tab').forEach(b => b.classList.toggle('active', b === t));
            _renderPqTab();
        };
    });
    _loadPqMolds().then(() => _renderPqTab());
}

export async function _loadPqMolds() {
    try {
        const res = await fetch('/api/factory_structure');
        const struct = await res.json();
        const line = (struct.production_lines || []).find((l: { id: string; }) => l.id === _pqLineId);
        _pqMolds = line ? (line.equipment || []).filter((e: { machine_type: string; }) => e.machine_type === 'mold') : [];
    } catch (e) { _pqMolds = []; }
    const sel = document.getElementById('pq-mold-selector');
    if (_pqMolds.length > 0) {
        sel!.innerHTML = `<select id="pq-mold-sel" class="dash-filter-sel" style="max-width:300px">
            <option value="">Toate matrițele</option>
            ${_pqMolds.map(m => `<option value="${m.id}">${m.name || m.id}</option>`).join('')}
        </select>`;
        document.getElementById('pq-mold-sel')!.onchange = e => { _pqMoldId = (e.target as HTMLInputElement).value; _renderPqTab(); };
    } else {
        sel!.innerHTML = '';
    }
}

export async function _renderPqTab() {
    const el = document.getElementById('pq-content');
    const ismaster = (state.currentUser || {}).role === 'master';
    if (_pqTab === 'spec') await _pqRenderSpec(el, ismaster);
    else if (_pqTab === 'images') await _pqRenderImages(el, ismaster);
    else if (_pqTab === 'video') await _pqRenderVideo(el, ismaster);
    else if (_pqTab === 'check') await _pqRenderCheck(el, ismaster);
    else if (_pqTab === 'history') await _pqRenderHistory(el);
}

export async function _pqFetchDocs(category: string): Promise<QualityDoc[]> {
    let url = `/api/quality_docs?line_id=${_pqLineId}&category=${category}`;
    if (_pqMoldId) url += `&mold_id=${_pqMoldId}`;
    const res = await fetch(url);
    return await res.json();
}

export function _pqUploadForm(category: string, acceptTypes: string, el?: unknown) {
    return `<div class="pq-upload-form" style="margin-top:16px;padding:12px;border:1px dashed var(--border);border-radius:8px">
        <strong>Încarcă document</strong>
        <form onsubmit="return _pqUpload(event,'${category}')">
            <input type="file" name="file" accept="${acceptTypes}" required style="margin:8px 0;display:block">
            ${_pqMolds.length ? `<select name="mold_id" style="margin-bottom:8px"><option value="">General linie</option>${_pqMolds.map(m=>`<option value="${m.id}" ${_pqMoldId===m.id?'selected':''}>${m.name||m.id}</option>`).join('')}</select>` : ''}
            <input type="text" name="description" placeholder="Descriere (opțional)" style="width:100%;margin-bottom:8px;padding:6px;border:1px solid var(--border);border-radius:4px;background:var(--bg-card);color:var(--text)">
            <button type="submit" class="dash-btn dash-btn-detail" style="font-size:.85rem">Încarcă</button>
        </form>
    </div>`;
}

export async function _pqUpload(e: SubmitEvent, category: string) {
    e.preventDefault();
    const form = e.target as HTMLFormElement;   // câmpurile după nume: form.file, form.mold_id, form.description
    const fd = new FormData();
    fd.append('file', form.file.files[0]);
    fd.append('line_id', String(_pqLineId));   // FormData face oricum String(); tipul cere text
    fd.append('mold_id', form.mold_id ? form.mold_id.value : (_pqMoldId || ''));
    fd.append('doc_category', category);
    fd.append('description', form.description.value);
    fd.append('uploaded_by', (state.currentUser || {}).username || '');
    try {
        const res = await fetch('/api/quality_docs', { method: 'POST', body: fd });
        const data = await res.json();
        if (data.ok) { _renderPqTab(); } else { alert(data.error || 'Eroare upload'); }
    } catch (err) { alert('Eroare: ' + (err as Error).message); }
    return false;
}

export async function _pqDeleteDoc(id: number) {
    if (!await askConfirm('Ștergi acest document?')) return;
    await fetch(`/api/quality_docs/${id}`, { method: 'DELETE' });
    _renderPqTab();
}

export async function _pqRenderSpec(el: HTMLElement|null, ismaster: boolean) {
    const docs = await _pqFetchDocs('fisa_tehnica');
    let html = '<h3 style="margin:0 0 12px">Fișă Tehnică / Specificații</h3>';
    if (docs.length === 0) {
        html += '<div class="alarm-empty">Nicio fișă tehnică încărcată.</div>';
    } else {
        docs.forEach((d: QualityDoc) => {
            html += `<div class="pq-doc-row">
                <a href="/api/quality_docs/${d.id}/file" target="_blank" class="pq-doc-link">📄 ${d.original_name || d.filename}</a>
                <span class="pq-doc-meta">${d.description || ''} ${d.mold_id ? '('+d.mold_id+')' : ''}</span>
                ${ismaster ? `<button class="btn-danger-sm" onclick="_pqDeleteDoc(${d.id})">✕</button>` : ''}
            </div>`;
        });
    }
    if (ismaster) html += _pqUploadForm('fisa_tehnica', '.pdf,.doc,.docx');
    el!.innerHTML = html;
}

export async function _pqRenderImages(el: HTMLElement|null, ismaster: boolean) {
    const docsOk = await _pqFetchDocs('imagine_ok');
    const docsNok = await _pqFetchDocs('imagine_nok');
    let html = '<h3 style="margin:0 0 12px">Imagini Produs</h3>';
    html += '<div class="pq-img-section"><h4 class="pq-img-ok-label">Produs OK (referință)</h4><div class="pq-img-grid">';
    if (docsOk.length === 0) html += '<div class="alarm-empty" style="grid-column:1/-1">Nicio imagine OK încărcată.</div>';
    docsOk.forEach((d: QualityDoc) => {
        html += `<div class="pq-img-card pq-img-ok">
            <img src="/api/quality_docs/${d.id}/file" alt="${d.description||'OK'}" onclick="document.getElementById('lightbox-img').src=this.src;document.getElementById('lightbox-overlay').style.display='flex'">
            <div class="pq-img-caption">${d.description || d.original_name} ${d.mold_id ? '<small>('+d.mold_id+')</small>' : ''}
            ${ismaster ? `<button class="btn-danger-sm" onclick="_pqDeleteDoc(${d.id})">✕</button>` : ''}</div>
        </div>`;
    });
    html += '</div></div>';
    html += '<div class="pq-img-section"><h4 class="pq-img-nok-label">Produs NOK (defecte)</h4><div class="pq-img-grid">';
    if (docsNok.length === 0) html += '<div class="alarm-empty" style="grid-column:1/-1">Nicio imagine NOK încărcată.</div>';
    docsNok.forEach((d: QualityDoc) => {
        html += `<div class="pq-img-card pq-img-nok">
            <img src="/api/quality_docs/${d.id}/file" alt="${d.description||'NOK'}" onclick="document.getElementById('lightbox-img').src=this.src;document.getElementById('lightbox-overlay').style.display='flex'">
            <div class="pq-img-caption">${d.description || d.original_name} ${d.mold_id ? '<small>('+d.mold_id+')</small>' : ''}
            ${ismaster ? `<button class="btn-danger-sm" onclick="_pqDeleteDoc(${d.id})">✕</button>` : ''}</div>
        </div>`;
    });
    html += '</div></div>';
    if (ismaster) {
        html += '<div style="display:flex;gap:16px;flex-wrap:wrap">';
        html += `<div style="flex:1;min-width:250px">${_pqUploadForm('imagine_ok', 'image/*').replace('Încarcă document', 'Imagine produs OK')}</div>`;
        html += `<div style="flex:1;min-width:250px">${_pqUploadForm('imagine_nok', 'image/*').replace('Încarcă document', 'Imagine produs NOK')}</div>`;
        html += '</div>';
    }
    el!.innerHTML = html;
}

export async function _pqRenderVideo(el: HTMLElement|null, ismaster: boolean) {
    const docs = await _pqFetchDocs('video_verificare');
    let html = '<h3 style="margin:0 0 12px">Video Verificare</h3>';
    if (docs.length === 0) {
        html += '<div class="alarm-empty">Niciun video demonstrativ încărcat.</div>';
    } else {
        docs.forEach((d: QualityDoc) => {
            html += `<div class="pq-video-card">
                <video controls preload="metadata" style="width:100%;max-height:400px;border-radius:8px;background:#000">
                    <source src="/api/quality_docs/${d.id}/file" type="video/${(d.filename||'').split('.').pop()}">
                </video>
                <div class="pq-video-caption">${d.description || d.original_name} ${d.mold_id ? '('+d.mold_id+')' : ''}
                ${ismaster ? `<button class="btn-danger-sm" onclick="_pqDeleteDoc(${d.id})">✕</button>` : ''}</div>
            </div>`;
        });
    }
    const procDocs = await _pqFetchDocs('procedura');
    if (procDocs.length > 0) {
        html += '<h4 style="margin:16px 0 8px">Proceduri verificare</h4>';
        procDocs.forEach((d: QualityDoc) => {
            html += `<div class="pq-doc-row">
                <a href="/api/quality_docs/${d.id}/file" target="_blank" class="pq-doc-link">📋 ${d.original_name || d.filename}</a>
                <span class="pq-doc-meta">${d.description || ''}</span>
                ${ismaster ? `<button class="btn-danger-sm" onclick="_pqDeleteDoc(${d.id})">✕</button>` : ''}
            </div>`;
        });
    }
    if (ismaster) {
        html += '<div style="display:flex;gap:16px;flex-wrap:wrap">';
        html += `<div style="flex:1;min-width:250px">${_pqUploadForm('video_verificare', 'video/*').replace('Încarcă document', 'Video demonstrativ')}</div>`;
        html += `<div style="flex:1;min-width:250px">${_pqUploadForm('procedura', '.pdf,.doc,.docx').replace('Încarcă document', 'Procedură verificare PDF')}</div>`;
        html += '</div>';
    }
    el!.innerHTML = html;
}

export async function _pqRenderCheck(el: HTMLElement|null, ismaster: boolean) {
    let url = `/api/quality_templates?line_id=${_pqLineId}`;
    if (_pqMoldId) url += `&mold_id=${_pqMoldId}`;
    const templates = await (await fetch(url)).json();
    let html = '<h3 style="margin:0 0 12px">Fișă Control Calitate</h3>';
    if (templates.length === 0) {
        html += '<div class="alarm-empty">Niciun șablon de fișă control definit.' + (ismaster ? ' Adaugă unul mai jos.' : ' Contactează masterul pentru configurare.') + '</div>';
    }
    for (const t of templates) {
        html += `<div class="pq-check-template">
            <div class="pq-check-header"><strong>${t.name}</strong> ${t.mold_id ? '<small>('+t.mold_id+')</small>' : '<small>(general)</small>'}
            ${ismaster ? `<button class="btn-danger-sm" onclick="_pqDeleteTemplate(${t.id})">Șterge</button>` : ''}</div>
            <form onsubmit="return _pqSubmitCheck(event, ${t.id})" class="pq-check-form">
                <table class="pq-check-table"><thead><tr><th>Parametru</th><th>Unitate</th><th>Min</th><th>Nominal</th><th>Max</th><th>Măsurat</th><th>Status</th></tr></thead><tbody>`;
        (t.fields || []).forEach((f: QualityField) => {
            if (f.field_type === 'numeric') {
                html += `<tr>
                    <td>${f.field_name}</td><td>${f.unit || ''}</td>
                    <td class="pq-val-min">${f.min_value != null ? f.min_value : '—'}</td>
                    <td class="pq-val-nom">${f.nominal_value != null ? f.nominal_value : '—'}</td>
                    <td class="pq-val-max">${f.max_value != null ? f.max_value : '—'}</td>
                    <td><input type="number" step="any" name="field_${f.id}" class="pq-input-measure" data-min="${f.min_value}" data-max="${f.max_value}" oninput="_pqCheckValue(this)" required></td>
                    <td class="pq-cell-status"></td>
                </tr>`;
            } else if (f.field_type === 'boolean') {
                html += `<tr><td>${f.field_name}</td><td colspan="4"></td>
                    <td><select name="field_${f.id}" class="pq-input-measure"><option value="1">OK</option><option value="0">NOK</option></select></td>
                    <td></td></tr>`;
            } else {
                html += `<tr><td>${f.field_name}</td><td colspan="4">${f.unit || ''}</td>
                    <td><input type="text" name="field_${f.id}" class="pq-input-measure"></td><td></td></tr>`;
            }
        });
        html += `</tbody></table>
            <div style="display:flex;gap:12px;align-items:center;margin-top:12px;flex-wrap:wrap">
                <label style="cursor:pointer"><input type="file" name="photo" accept="image/*" capture="camera" style="max-width:200px"> Foto validare</label>
                <input type="text" name="notes" placeholder="Observații" style="flex:1;min-width:150px;padding:6px;border:1px solid var(--border);border-radius:4px;background:var(--bg-card);color:var(--text)">
                <button type="submit" class="dash-btn dash-btn-detail">Salvează verificare</button>
            </div>
            <input type="hidden" name="template_id" value="${t.id}">
        </form></div>`;
    }
    if (ismaster) {
        html += `<div class="pq-upload-form" style="margin-top:16px;padding:12px;border:1px dashed var(--border);border-radius:8px">
            <strong>Adaugă șablon fișă control</strong>
            <form onsubmit="return _pqCreateTemplate(event)">
                <input type="text" name="tpl_name" placeholder="Nume fișă (ex: Control Capac 48mm)" required style="width:100%;margin:8px 0;padding:6px;border:1px solid var(--border);border-radius:4px;background:var(--bg-card);color:var(--text)">
                ${_pqMolds.length ? `<select name="tpl_mold"><option value="">General linie</option>${_pqMolds.map(m=>`<option value="${m.id}" ${_pqMoldId===m.id?'selected':''}>${m.name||m.id}</option>`).join('')}</select>` : ''}
                <div id="pq-tpl-fields" style="margin:8px 0"></div>
                <button type="button" class="dash-btn dash-btn-ai" style="font-size:.8rem;margin-bottom:8px" onclick="_pqAddField()">+ Adaugă câmp</button>
                <div style="display:flex;gap:8px;align-items:center">
                    <label>Verificare la fiecare: <input type="number" name="sched_hours" placeholder="ore" style="width:60px"> h</label>
                    <label>sau <input type="number" name="sched_cycles" placeholder="cicluri" style="width:80px"> buc</label>
                </div>
                <button type="submit" class="dash-btn dash-btn-detail" style="margin-top:8px;font-size:.85rem">Creează șablon</button>
            </form>
        </div>`;
    }
    el!.innerHTML = html;
}

export function _pqAddField() {
    const container = document.getElementById('pq-tpl-fields');
    const idx = container!.children.length;
    const div = document.createElement('div');
    div.className = 'pq-field-row';
    div.innerHTML = `<input type="text" name="fname_${idx}" placeholder="Parametru" required style="width:120px">
        <select name="ftype_${idx}"><option value="numeric">Numeric</option><option value="boolean">OK/NOK</option><option value="text">Text</option></select>
        <input type="text" name="funit_${idx}" placeholder="Unitate" style="width:50px">
        <input type="number" step="any" name="fmin_${idx}" placeholder="Min" style="width:60px">
        <input type="number" step="any" name="fnom_${idx}" placeholder="Nominal" style="width:60px">
        <input type="number" step="any" name="fmax_${idx}" placeholder="Max" style="width:60px">
        <button type="button" onclick="this.parentElement.remove()" style="color:var(--danger);background:none;border:none;cursor:pointer;font-size:1.1rem">✕</button>`;
    container!.appendChild(div);
}

export async function _pqCreateTemplate(e: SubmitEvent) {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const fields: Partial<QualityField>[] = [];
    const fieldRows = form.querySelectorAll('.pq-field-row');
    fieldRows.forEach((row: Element, i: number) => {
        const fname = form[`fname_${i}`]?.value;
        if (!fname) return;
        fields.push({
            field_name: fname,
            field_type: form[`ftype_${i}`]?.value || 'numeric',
            unit: form[`funit_${i}`]?.value || '',
            min_value: parseFloat(form[`fmin_${i}`]?.value) || null,
            nominal_value: parseFloat(form[`fnom_${i}`]?.value) || null,
            max_value: parseFloat(form[`fmax_${i}`]?.value) || null,
        });
    });
    const body = {
        line_id: _pqLineId,
        mold_id: form.tpl_mold?.value || _pqMoldId || null,
        name: form.tpl_name.value,
        created_by: (state.currentUser || {}).username || '',
        fields
    };
    const res = await fetch('/api/quality_templates', { method: 'POST', headers: {'Content-Type': 'application/json'}, body: JSON.stringify(body) });
    const data = await res.json();
    if (data.ok && data.id) {
        const sh = parseFloat(form.sched_hours?.value) || null;
        const sc = parseInt(form.sched_cycles?.value) || null;
        if (sh || sc) {
            await fetch('/api/quality_schedules', { method: 'POST', headers: {'Content-Type':'application/json'}, body: JSON.stringify({
                line_id: _pqLineId, mold_id: body.mold_id, template_id: data.id, interval_hours: sh, interval_cycles: sc
            })});
        }
        _renderPqTab();
    } else { alert(data.error || 'Eroare creare șablon'); }
    return false;
}

export async function _pqDeleteTemplate(tid: number) {
    if (!await askConfirm('Ștergi acest șablon de fișă control și toate datele asociate?')) return;
    await fetch(`/api/quality_templates/${tid}`, { method: 'DELETE' });
    _renderPqTab();
}

export function _pqCheckValue(input: HTMLInputElement) {
    const min = parseFloat(input.dataset.min as string);
    const max = parseFloat(input.dataset.max as string);
    const val = parseFloat(input.value);
    const cell = input.closest('tr')!.querySelector<HTMLElement>('.pq-cell-status')!;
    if (isNaN(val)) { cell.textContent = ''; input.style.borderColor = ''; return; }
    if (!isNaN(min) && val < min) { cell.textContent = '✗'; cell.style.color = 'var(--danger)'; input.style.borderColor = 'var(--danger)'; }
    else if (!isNaN(max) && val > max) { cell.textContent = '✗'; cell.style.color = 'var(--danger)'; input.style.borderColor = 'var(--danger)'; }
    else { cell.textContent = '✓'; cell.style.color = 'var(--success)'; input.style.borderColor = 'var(--success)'; }
}

export async function _pqSubmitCheck(e: SubmitEvent, templateId: number) {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const fd = new FormData();
    fd.append('template_id', String(templateId));
    fd.append('line_id', String(_pqLineId));   // FormData face oricum String(); tipul cere text
    fd.append('mold_id', _pqMoldId || '');
    fd.append('checked_by', (state.currentUser || {}).username || '');
    fd.append('notes', form.notes.value || '');
    const values: Record<string, string> = {};
    let allOk = true;
    form.querySelectorAll<HTMLInputElement>('.pq-input-measure').forEach((inp) => {
        const fid = inp.name.replace('field_', '');
        values[fid] = inp.value;
        const min = parseFloat(inp.dataset?.min as string);
        const max = parseFloat(inp.dataset?.max as string);
        const v = parseFloat(inp.value);
        if (!isNaN(min) && !isNaN(v) && v < min) allOk = false;
        if (!isNaN(max) && !isNaN(v) && v > max) allOk = false;
    });
    fd.append('values_json', JSON.stringify(values));
    fd.append('result', allOk ? 'ok' : 'nok');
    if (form.photo && form.photo.files[0]) fd.append('photo', form.photo.files[0]);
    const res = await fetch('/api/quality_checks', { method: 'POST', body: fd });
    const data = await res.json();
    if (data.ok) {
        alert(allOk ? 'Verificare salvată - CONFORME' : 'Verificare salvată - NECONFORME! Verificați valorile.');
        _pqTab = 'history';
        document.querySelectorAll('#pq-tabs .detail-tab').forEach(t => t.classList.toggle('active', (t as HTMLElement).dataset.pqtab === 'history'));
        _renderPqTab();
    } else { alert(data.error || 'Eroare salvare'); }
    return false;
}

export async function _pqRenderHistory(el: HTMLElement|null) {
    let url = `/api/quality_checks?line_id=${_pqLineId}&limit=30`;
    if (_pqMoldId) url += `&mold_id=${_pqMoldId}`;
    const checks: QualityCheck[] = await (await fetch(url)).json();
    const templates: QualityTemplate[] = await (await fetch(`/api/quality_templates?line_id=${_pqLineId}`)).json();
    const tplMap: Record<number, QualityTemplate> = {};
    templates.forEach((t) => { tplMap[t.id] = t; });
    let html = '<h3 style="margin:0 0 12px">Istoric Verificări Calitate</h3>';
    if (checks.length === 0) {
        html += '<div class="alarm-empty">Nicio verificare efectuată.</div>';
    } else {
        html += '<table class="detail-revision-table"><thead><tr><th>Data</th><th>Fișă</th><th>Verificat de</th><th>Rezultat</th><th>Observații</th><th>Foto</th></tr></thead><tbody>';
        checks.forEach((c) => {
            const tpl = tplMap[c.template_id as number];
            const rClass = c.result === 'ok' ? 'pq-result-ok' : c.result === 'nok' ? 'pq-result-nok' : '';
            html += `<tr class="${rClass}">
                <td>${(c.checked_at || '').replace('T', ' ').substring(0, 16)}</td>
                <td>${tpl ? tpl.name : '#' + c.template_id}</td>
                <td>${c.checked_by || '—'}</td>
                <td><strong>${c.result === 'ok' ? 'CONFORM' : c.result === 'nok' ? 'NECONFORM' : c.result}</strong></td>
                <td>${c.notes || '—'}</td>
                <td>${c.photo_filename ? `<img src="/uploads/quality/${c.photo_filename}" style="width:40px;height:40px;object-fit:cover;border-radius:4px;cursor:pointer" onclick="document.getElementById('lightbox-img').src=this.src;document.getElementById('lightbox-overlay').style.display='flex'">` : '—'}</td>
            </tr>`;
        });
        html += '</tbody></table>';
    }
    el!.innerHTML = html;
}

/* Quality alerts in dashboard */
export async function _loadQualityAlerts() {
    try {
        const res = await fetch('/api/quality_alerts');
        return await res.json();
    } catch (e) { return []; }
}
